import { Container, Graphics, Text } from 'pixi.js';
import { COLORS, textStyle, drawPanel } from './theme';

const BAR_WIDTH = 200;
const BAR_HEIGHT = 16;
const MAX_WIND = 10;
const COMPASS_RADIUS = 19;
const COMPASS_CX = BAR_WIDTH / 2;
const COMPASS_CY = COMPASS_RADIUS + 4;

/**
 * Wind readout (Fusion chrome): a beveled panel containing either a GunBound
 * style horizontal strength bar (classic mode) or a 360-degree compass dial
 * (vector mode), with a bold "WIND n" label. Re-skinned to match the HUD.
 */
export class WindIndicator {
  container: Container;
  private panel: Graphics;
  private background: Graphics;
  private compassBg: Graphics;
  private fill: Graphics;
  private arrow: Graphics;
  private label: Text;
  private lastMode: 'bar' | 'compass' | null = null;

  constructor() {
    this.container = new Container();

    this.panel = new Graphics();
    this.container.addChild(this.panel);

    this.background = new Graphics();
    this.background
      .roundRect(0, 6, BAR_WIDTH, BAR_HEIGHT, 5)
      .fill({ color: 0x05101f, alpha: 0.85 });
    this.background
      .roundRect(0, 6, BAR_WIDTH, BAR_HEIGHT, 5)
      .stroke({ color: COLORS.stroke, width: 1.5 });
    this.background
      .moveTo(BAR_WIDTH / 2, 6)
      .lineTo(BAR_WIDTH / 2, 6 + BAR_HEIGHT)
      .stroke({ color: COLORS.bevelLight, width: 2, alpha: 0.7 });
    this.container.addChild(this.background);

    this.compassBg = this.createCompassBackground();
    this.compassBg.visible = false;
    this.container.addChild(this.compassBg);

    this.fill = new Graphics();
    this.container.addChild(this.fill);

    this.arrow = new Graphics();
    this.container.addChild(this.arrow);

    this.label = new Text({
      text: 'WIND',
      style: textStyle({ size: 14, weight: '700', display: true }),
    });
    this.label.anchor.set(0.5, 0);
    this.label.position.set(BAR_WIDTH / 2, BAR_HEIGHT + 10);
    this.container.addChild(this.label);

    this.update(0);
  }

  private createCompassBackground(): Graphics {
    const g = new Graphics();
    g.circle(COMPASS_CX, COMPASS_CY, COMPASS_RADIUS).fill({ color: 0x05101f, alpha: 0.85 });
    g.circle(COMPASS_CX, COMPASS_CY, COMPASS_RADIUS).stroke({ color: COLORS.gold, width: 2 });
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2;
      g.moveTo(
        COMPASS_CX + Math.cos(a) * (COMPASS_RADIUS - 4),
        COMPASS_CY + Math.sin(a) * (COMPASS_RADIUS - 4),
      );
      g.lineTo(
        COMPASS_CX + Math.cos(a) * (COMPASS_RADIUS - 1),
        COMPASS_CY + Math.sin(a) * (COMPASS_RADIUS - 1),
      );
    }
    g.stroke({ color: COLORS.gold, width: 2, alpha: 0.7 });
    g.circle(COMPASS_CX, COMPASS_CY, 2).fill({ color: COLORS.gold });
    return g;
  }

  update(windValue: number, windY = 0, maxStrength = MAX_WIND, vectorMode = false): void {
    this.fill.clear();

    const compass = vectorMode || windY !== 0;
    const mode = compass ? 'compass' : 'bar';
    this.background.visible = !compass;
    this.compassBg.visible = compass;

    if (mode !== this.lastMode) {
      this.lastMode = mode;
      this.panel.clear();
      if (compass) {
        drawPanel(this.panel, COMPASS_CX - 34, 0, 68, COMPASS_RADIUS * 2 + 8, {
          radius: 12,
          alpha: 0.66,
        });
      } else {
        drawPanel(this.panel, -8, 0, BAR_WIDTH + 16, BAR_HEIGHT + 30, { radius: 12, alpha: 0.66 });
      }
    }

    if (compass) {
      this.renderVector(windValue, windY, maxStrength);
      return;
    }

    this.label.anchor.set(0.5, 0);
    this.label.position.set(BAR_WIDTH / 2, BAR_HEIGHT + 10);

    const normalizedWind = Math.max(-maxStrength, Math.min(maxStrength, windValue));
    this.label.text = `WIND ${Math.abs(Math.round(windValue))}`;
    const fillWidth = (Math.abs(normalizedWind) / maxStrength) * (BAR_WIDTH / 2 - 4);

    if (normalizedWind !== 0) {
      const startX = normalizedWind > 0 ? BAR_WIDTH / 2 : BAR_WIDTH / 2 - fillWidth;
      // Stronger wind trends from cyan to gold.
      const strong = Math.abs(normalizedWind) / maxStrength > 0.6;
      this.fill
        .roundRect(startX, 8, fillWidth, BAR_HEIGHT - 4, 3)
        .fill({ color: strong ? COLORS.gold : COLORS.cyan, alpha: 0.95 });
    }

    this.arrow.clear();
    if (normalizedWind !== 0) {
      const arrowX =
        normalizedWind > 0 ? BAR_WIDTH / 2 + fillWidth + 8 : BAR_WIDTH / 2 - fillWidth - 8;
      const arrowDir = normalizedWind > 0 ? 1 : -1;
      const cy = 6 + BAR_HEIGHT / 2;
      this.arrow.moveTo(arrowX, cy);
      this.arrow.lineTo(arrowX - arrowDir * 7, cy - 5);
      this.arrow.lineTo(arrowX - arrowDir * 7, cy + 5);
      this.arrow.closePath();
      this.arrow.fill({ color: COLORS.white });
    }
  }

  /** 360-degree wind: compass with a needle pointing where the wind blows. */
  private renderVector(windX: number, windY: number, maxStrength: number): void {
    this.arrow.clear();
    const magnitude = Math.hypot(windX, windY);
    this.label.text = `${Math.round(magnitude)}`;
    this.label.anchor.set(0.5, 0.5);
    this.label.position.set(COMPASS_CX, COMPASS_CY + COMPASS_RADIUS + 12);
    if (magnitude === 0) return;

    const angle = Math.atan2(windY, windX);
    const len = 5 + Math.min(1, magnitude / maxStrength) * (COMPASS_RADIUS - 8);
    const ex = COMPASS_CX + Math.cos(angle) * len;
    const ey = COMPASS_CY + Math.sin(angle) * len;
    const tx = COMPASS_CX - Math.cos(angle) * (len * 0.5);
    const ty = COMPASS_CY - Math.sin(angle) * (len * 0.5);

    this.arrow.moveTo(tx, ty).lineTo(ex, ey).stroke({ width: 3, color: COLORS.cyan });
    const headAngle = 0.5;
    const headLen = 6;
    this.arrow
      .moveTo(ex, ey)
      .lineTo(
        ex - Math.cos(angle - headAngle) * headLen,
        ey - Math.sin(angle - headAngle) * headLen,
      );
    this.arrow
      .moveTo(ex, ey)
      .lineTo(
        ex - Math.cos(angle + headAngle) * headLen,
        ey - Math.sin(angle + headAngle) * headLen,
      );
    this.arrow.stroke({ width: 3, color: COLORS.cyan });
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
